import { Sun, Moon } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/lib/theme-provider'
import { cn } from '@/lib/utils'

interface ThemeToggleProps {
  'data-testid'?: string
}

export function ThemeToggle(props: ThemeToggleProps) {
  const { t } = useTranslation('common')
  const { theme, toggleTheme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t('themeToggle')}
      data-testid={props['data-testid'] ?? 'theme-toggle'}
      onClick={toggleTheme}
      className={cn(
        'relative inline-flex h-8 w-[60px] shrink-0 cursor-pointer items-center rounded-full',
        'border-2 border-transparent bg-white/50 dark:bg-muted transition-colors',
        'opacity-90 hover:opacity-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center" aria-hidden>
        <Sun className="size-3.5 text-muted-foreground" />
      </span>
      <span className="absolute right-2 flex size-4 items-center justify-center" aria-hidden>
        <Moon className="size-3.5 text-foreground/70 dark:text-muted-foreground" />
      </span>

      <span
        className={cn(
          'pointer-events-none flex size-6 items-center justify-center rounded-full shadow-sm',
          'transition-transform duration-200 ease-out',
          // colors come from existing semantic tokens via the `dark:` Tailwind variant —
          // light mode shows the sun thumb on a card surface; dark mode shows the moon
          // thumb on the always-mint CTA color.
          'bg-card text-foreground dark:bg-cta dark:text-cta-foreground',
          // structural: thumb position only
          isDark ? 'translate-x-8' : 'translate-x-1',
        )}
      >
        {/* structural: Sun ↔ Moon icon swap */}
        {isDark ? (
          <Moon className="size-3.5" />
        ) : (
          <Sun className="size-3.5" />
        )}
      </span>
    </button>
  )
}
